import { useState, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { VerticalProvider, useVertical } from './context/VerticalContext';
import { RoleProvider, useRole } from './context/RoleContext';
import { FinancialProvider, useFinancialContext } from './context/FinancialContext';
import { PowerStack } from './components/dashboard/PowerStack';
import { clearAllJobDrafts } from './lib/jobDraft';
import { DashboardLayout } from './components/dashboard/DashboardLayout';
import WorkshopHub from './modules/mechanic/Workshop/WorkshopHub';
import { OnboardingCheck } from './components/auth/OnboardingCheck';
import { LoginScreen } from './components/auth/LoginScreen';
import { ResetPasswordScreen } from './components/auth/ResetPasswordScreen';
import { ControlTowerLayout } from './modules/ControlTower/ControlTowerLayout';
import { supabase } from './lib/supabase';
import { captureRefCode } from './lib/referral';
import type { Session } from '@supabase/supabase-js';
import { Loader2 } from 'lucide-react';

import { ProfilePage } from './modules/Profile/ProfilePage';
import CustomersPage from './modules/mechanic/Customers/CustomersPage';
import CustomerProfile from './modules/mechanic/Customers/CustomerProfile';
import FollowUpsPage from './modules/mechanic/Customers/lists/FollowUpsPage';
import BrowsePage from './modules/mechanic/Customers/lists/BrowsePage';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ExpensesHub } from './modules/Financials/ExpensesHub';
import BusinessHealth from './pages/BusinessHealth';
import NotificationsPage from './pages/NotificationsPage';
import HelpCentre from './pages/HelpCentre';
import MotRemindersPage from './pages/MotRemindersPage';
import PublicQuoteView from './modules/mechanic/Quotes/PublicQuoteView';
import InvoiceHub from './modules/mechanic/Invoice/InvoiceHub';
import PortalPage from './modules/Portal/PortalPage';
import { ConnectReturn } from './pages/ConnectReturn';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancelled from './pages/PaymentCancelled';
import PaymentLanding from './pages/PaymentLanding';

import SupplierAccountsPage from './modules/mechanic/Returns/SupplierAccountsPage';
import SchedulePage from './pages/SchedulePage';
import { VatWarningBanner } from './components/dashboard/VatWarningBanner';
import { TaxBandBanner } from './components/dashboard/TaxBandBanner';
import { GettingStartedChecklist } from './components/onboarding/GettingStartedChecklist';
import { HelpHint } from './components/ui/HelpHint';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { BlogAdmin } from './modules/Blog/BlogAdmin';
import { reportError } from './lib/errors';
import { ForcedTheme } from './context/ThemeContext';
import { useToday } from './hooks/useToday';
import { monthKey, monthLabel } from './lib/periods';

function DashboardHeader() {
  const { molded, orgCreatedAt } = useVertical();
  const { isHistoricalView, selectedMonth, setSelectedMonth } = useFinancialContext();
  const today = useToday();

  // "Tax year to date" spans several months, so the month in progress gets its
  // own entry: it is the only way to see this month on its own.
  const currentMonth = monthKey(today);
  const isCurrentMonth = selectedMonth === currentMonth;

  const generateMonthOptions = () => {
    const options = [];
    const date = new Date(today.getFullYear(), today.getMonth(), 1);

    const earliestDate = orgCreatedAt ? new Date(orgCreatedAt) : null;
    if (earliestDate) {
        // Set to the first day of the earliest month to ensure the month they joined is included
        earliestDate.setDate(1);
        earliestDate.setHours(0, 0, 0, 0);
    }
    
    for (let i = 0; i < 24; i++) {
        // If we have an earliest date and we've gone past it, break out of the loop
        if (earliestDate && date < earliestDate) {
            break;
        }

        const value = monthKey(date);
        const month = date.toLocaleString('en-GB', { month: 'short', year: 'numeric' });
        const label = value === currentMonth ? `${month} (so far)` : month;
        options.push({ value, label });
        date.setMonth(date.getMonth() - 1);
    }
    return options;
  };

  return (
    <header className="mb-5 lg:mb-6 flex flex-col md:flex-row md:items-end justify-between gap-4">
      <div className="min-w-0">
        <p className="fd-eyebrow mb-2">
          {isHistoricalView && !isCurrentMonth
            ? 'History'
            : `Today · ${today.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}`}
        </p>
        <h2 className="fd-page-title">
          {molded.jargon.trade_label} Dashboard
        </h2>
        <p className="fd-page-subtitle">
          {isCurrentMonth
            ? 'This month so far.'
            : isHistoricalView && selectedMonth
              ? `Viewing ${monthLabel(selectedMonth)} on its own.`
              : 'Welcome back, Boss.'}
        </p>
      </div>
      <div className="flex items-center gap-3 self-start md:self-auto">
        <HelpHint slug="safe-to-spend" label="How Safe-to-Spend works" />
        <select
          value={selectedMonth || ''}
          onChange={(e) => setSelectedMonth(e.target.value || null)}
          aria-label="Period"
          className="fd-input fd-select-pill w-auto min-w-[10rem]"
        >
          <option value="">Tax year to date</option>
          {generateMonthOptions().map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
    </header>
  );
}

function DashboardHome() {
  // The accountant has no business on the workshop dashboard — it is jobs,
  // appointments and the four money-generating actions. Business Health is the
  // same financial picture without the floor. Redirect rather than gate the
  // route: `/` is the app's landing page for every other role, and gating it
  // outright would displace mechanics too.
  const { role, loading } = useRole();
  if (!loading && role === 'admin') {
    return <Navigate to="/business-health" replace />;
  }

  // The four money-generating actions + all dashboard modals now live inside
  // PowerStack (it owns New Job / New Diagnostic / New Appointment / New Quote
  // and reads the ?action= deep-link params itself).
  return (
    <>
      {/* Notices first: a VAT registration duty must be seen before anything
          else. Two advisories share a row on a wide screen (.fd-notices). */}
      <div className="fd-notices">
        <VatWarningBanner />
        <TaxBandBanner />
      </div>
      <DashboardHeader />
      {/* Setup is context, not work: PowerStack places it after the jobs and
          the money on a phone, and in the rail beside them on a wide screen. */}
      <PowerStack setup={<GettingStartedChecklist />} />
    </>
  )
}



function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  // Track intentional sign-out to prevent onAuthStateChange from restoring session
  const signedOutForInviteRef = useRef(false);

  useEffect(() => {
    // Global Invite Interception
    const params = new URLSearchParams(window.location.search);
    const inviteToken = params.get('invite_token');
    if (inviteToken) {
      localStorage.setItem('pending_invite', inviteToken);
    }

    // Partner referral capture (?ref=CODE). Intercepted at the same point as the
    // invite token and for the same reason: it arrives on the landing URL and is
    // needed later, after auth, when the org is provisioned. Stored rather than
    // read from the URL at that point because the two can be days apart -- the
    // free tier means most partner-referred users sign up now and upgrade weeks
    // later. See lib/referral.ts and docs/decisions/partner-programme.md.
    captureRefCode(window.location.search);

    const initAuth = async () => {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      
      // CRITICAL: If there's a pending invite AND the user is logged in,
      // verify email match BEFORE rendering the dashboard.
      const pendingInvite = inviteToken || localStorage.getItem('pending_invite');
      if (pendingInvite && currentSession?.user) {
        try {
          let cleanToken = pendingInvite;
          const hexMatch = pendingInvite.match(/[0-9a-f]{32}/i);
          if (hexMatch) cleanToken = hexMatch[0];
          const uuidMatch = pendingInvite.match(/[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}/i);
          if (uuidMatch) cleanToken = uuidMatch[0];

          const { data: inviteDetails, error: inviteDetailsError } = await supabase.rpc('get_invite_details', { token_text: cleanToken });
          if (inviteDetailsError) reportError(inviteDetailsError, 'app.get-invite-details');
          
          if (inviteDetails && inviteDetails.valid && inviteDetails.email) {
            const inviteEmail = inviteDetails.email.toLowerCase().trim();
            const userEmail = (currentSession.user.email || '').toLowerCase().trim();
            
            if (inviteEmail !== userEmail) {
              signedOutForInviteRef.current = true; // Block onAuthStateChange
              localStorage.setItem('pending_invite', cleanToken);
              await supabase.auth.signOut();
              setSession(null);
              setLoading(false);
              return;
            }
          }
        } catch (_e) {
          // Could not verify invite email — proceed with existing session
        }
      }
      
      setSession(currentSession);
      setLoading(false);
    };

    initAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // A New Job draft holds customer contact details; it does not outlive
      // the session on this device (lib/jobDraft.ts).
      if (event === 'SIGNED_OUT') clearAllJobDrafts();
      // Don't restore session if we intentionally signed out for invite mismatch
      if (signedOutForInviteRef.current && session) {
        return;
      }
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Public Payment Pages — render BEFORE auth check (no login required)
  if (location.pathname.startsWith('/pay/')) {
    return (
      <Routes>
        <Route path="/pay/success" element={<PaymentSuccess />} />
        <Route path="/pay/cancelled" element={<PaymentCancelled />} />
        {/* Token-gated invoice link — the only public route to an invoice.
            The old /pay/invoice/:invoiceId route was retired on 2026-08-03
            (audit finding F-08): a raw invoice UUID was a bearer credential
            with no expiry and no revocation. */}
        <Route path="/pay/t/:token" element={<PaymentLanding />} />
      </Routes>
    );
  }

  // Password recovery — BEFORE the session gate on purpose. The recovery link
  // carries a token that supabase-js exchanges for a real session, so without
  // this intercept the user is dropped into the dashboard having never chosen a
  // password. Rendered in both the signed-in and signed-out states because the
  // exchange may or may not have completed by the time this runs.
  if (location.pathname.startsWith('/reset-password')) {
    return <ResetPasswordScreen />;
  }

  if (loading) {
    return (
      <div className="h-screen w-screen bg-canvas flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-fg-3" />
      </div>
    );
  }

  // Control Tower subdomain intercept
  const isTower = window.location.hostname.startsWith('tower');
  if (isTower) return <ForcedTheme theme="dark"><ControlTowerLayout /></ForcedTheme>;

  // Blog admin subdomain (blog.forgedash.uk) — dedicated writing/publishing
  // surface. BlogAdmin handles its own auth (Google OAuth) + platform-admin
  // gate, so it renders before the app session check.
  const isBlogAdmin = window.location.hostname.startsWith('blog');
  if (isBlogAdmin) return <ForcedTheme theme="dark"><BlogAdmin basePath="" /></ForcedTheme>;

  if (!session) {
    // Public Quote Route (Before Auth Check)
    if (location.pathname.startsWith('/quote/')) {
      return <PublicQuoteView />;
    }
    // Customer Portal (Magic Link Auth - No Supabase Session)
    if (location.pathname.startsWith('/portal')) {
      return <PortalPage />;
    }
    // Default unauthenticated view: System Login
    return <LoginScreen />;
  }

  // The customer portal is token-gated, not session-gated: a garage owner
  // opening their own customer's link while signed in must see the portal,
  // not the dashboard.
  if (location.pathname.startsWith('/portal')) {
    return <PortalPage />;
  }

  // Allow authenticated users to see quote view too (e.g. if they click the link while logged in)
  if (location.pathname.startsWith('/quote/')) {
    return <PublicQuoteView />;
  }

  // Blog admin — platform-admin-only, rendered outside org context/providers.
  if (location.pathname.startsWith('/admin/blog')) {
    return <ForcedTheme theme="dark"><BlogAdmin /></ForcedTheme>;
  }

  return (
    <RoleProvider>
      <VerticalProvider>
        <FinancialProvider>
          <Routes>
            {/* 1. COMMAND CENTRE (The Fortress) - Standalone Layout */}
            {/* 1. COMMAND CENTRE - Moved to tower.subdomain */}

            {/* Stripe Express onboarding return points — IMPORTANT: MUST BE OUTSIDE
                DASHBOARD LAYOUT. These are Stripe's `return_url` and `refresh_url`;
                the trader arrives here straight from the hosted onboarding form.
                /stripe-callback was the old Standard-OAuth redirect and is kept
                pointed at the same status check so a link already in flight, or
                bookmarked, still lands somewhere sensible. */}
            <Route path="/connect/complete" element={<ConnectReturn mode="complete" />} />
            <Route path="/connect/reauth" element={<ConnectReturn mode="reauth" />} />
            <Route path="/stripe-callback" element={<ConnectReturn mode="complete" />} />

            {/* 2. CIVILIAN APP - Dashboard Layout & Onboarding */}
            <Route path="/*" element={
              <OnboardingCheck>
                <DashboardLayout>
                  <Routes>
                    <Route path="/" element={<DashboardHome />} />
                    <Route path="/schedule" element={
                      <ProtectedRoute requiredPermission="workshop">
                        <SchedulePage />
                      </ProtectedRoute>
                    } />
                    <Route path="/workshop/*" element={
                      <ProtectedRoute requiredPermission="workshop">
                        <ErrorBoundary>
                          <WorkshopHub />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    } />
                    {/* Note: settings mapped to ProfilePage as admin is separate. */}
                    <Route path="/settings" element={
                      <ProtectedRoute requiredPermission="settings">
                        <ProfilePage />
                      </ProtectedRoute>
                    } />
                    <Route path="/profile" element={
                      <ProtectedRoute requiredPermission="settings">
                        <ProfilePage />
                      </ProtectedRoute>
                    } />
                    <Route path="/clients" element={
                      <ProtectedRoute requiredPermission="workshop">
                        <CustomersPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/expenses" element={
                      <ProtectedRoute requiredPermission="expenses">
                        <ExpensesHub />
                      </ProtectedRoute>
                    } />
                    <Route path="/business-health" element={
                      <ProtectedRoute requiredPermission="financial_reports">
                        <BusinessHealth />
                      </ProtectedRoute>
                    } />
                    {/* Help is deliberately ungated — every role needs it, and
                        individual articles filter themselves by permission. */}
                    <Route path="/help" element={<HelpCentre />} />
                    <Route path="/help/:slug" element={<HelpCentre />} />
                    <Route path="/notifications" element={<NotificationsPage />} />
                    <Route path="/reminders" element={
                      <ProtectedRoute requiredPermission="workshop">
                        <MotRemindersPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/mechanic/mot-reminders" element={
                      <ProtectedRoute requiredPermission="workshop">
                        <MotRemindersPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/mechanic/customers/:id" element={
                      <ProtectedRoute requiredPermission="workshop">
                        <CustomerProfile />
                      </ProtectedRoute>
                    } />
                    {/* Static segments before /clients/:id — React Router
                        ranks them higher regardless of order, but declaring
                        them first keeps the intent readable. */}
                    <Route path="/clients/follow-ups/:lane" element={
                      <ProtectedRoute requiredPermission="workshop">
                        <FollowUpsPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/clients/browse/:kind" element={
                      <ProtectedRoute requiredPermission="workshop">
                        <BrowsePage />
                      </ProtectedRoute>
                    } />
                    <Route path="/clients/:id" element={
                      <ProtectedRoute requiredPermission="workshop">
                        <CustomerProfile />
                      </ProtectedRoute>
                    } />
                    {/* `invoices`, not `workshop`: revenue is the accountant's
                        job, but the workshop floor is not. */}
                    <Route path="/invoices" element={
                      <ProtectedRoute requiredPermission="invoices">
                        <InvoiceHub />
                      </ProtectedRoute>
                    } />
                    {/* Supplier balances are financial: owner + accountant only.
                        supplier_accounts is in the RLS `financial` set, so a
                        mechanic reaching this route saw an empty page rather
                        than a refusal. */}
                    <Route path="/returns" element={
                      <ProtectedRoute requiredPermission="financial_reports">
                        <SupplierAccountsPage />
                      </ProtectedRoute>
                    } />

                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </DashboardLayout>
              </OnboardingCheck>
            } />
          </Routes>
        </FinancialProvider>
      </VerticalProvider>
    </RoleProvider>
  );
}

export default App;
